import { LOCAL_STORAGE, LOCAL_STORAGE_MIGRATE } from '~/constants';
import { compact, filter, isStringEmpty, join } from '~/utils';

/**
 * 单一企业下用户唯一标识
 */
interface CorpUser {
    tenantId: string;
    memberId: string;
}

// 用户唯一标识，相同账号，切换企业后还是相同的标识
interface User {
    userId: string;
}

const DELIMITER = '.';

export class StorageKeyManager {
    static genKey(...args: (string | number | undefined)[]) {
        return join(
            filter(args, (v) => {
                return !isStringEmpty(v);
            }),
            DELIMITER
        );
    }

    /**
     * 企业用户标识符固定顺序
     * @param options 企业用户标识参数
     */
    static genCorpUserKey(options: { tenantId?: string; userId?: string; memberId?: string }) {
        const { tenantId, userId, memberId } = options;
        return join(compact([userId, tenantId, memberId]), DELIMITER);
    }

    static getUpgradeTipKey(options: { username: string }) {
        return StorageKeyManager.genKey(LOCAL_STORAGE.UPGRADE_TIP, options.username);
    }

    static getContactLayoutWidthKey(options: CorpUser) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE_MIGRATE.CONTACT_LAYOUT_WIDTH,
            StorageKeyManager.genCorpUserKey(options)
        );
    }

    static getInviteMemberKey(options: { userId: string }) {
        return StorageKeyManager.genKey(LOCAL_STORAGE_MIGRATE.INVITE_MEMBER, options.userId);
    }

    static getMemberStatusFilterKey(options: User) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE_MIGRATE.MEMBER_STATUS_FILTER,
            StorageKeyManager.genCorpUserKey(options)
        );
    }

    /**
     * 自建插件调试代码
     * @param options 插件id
     */
    static getPluginPrivateDebugCodeKey(options: { pluginId: string }) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE_MIGRATE.PLUGIN_PRIVATE_DEBUG_CODE,
            options.pluginId
        );
    }

    /**
     * 将要过期的插件
     * @param options 企业用户标识参数
     */
    static getTrialDuePluginStorageKey(options: Pick<CorpUser, 'tenantId'>) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE_MIGRATE.TRIAL_DUE_PLUGIN,
            StorageKeyManager.genCorpUserKey(options)
        );
    }

    /** 开放平台路由缓存 */
    static getOpenPlatformTabKey(options: Pick<CorpUser, 'tenantId'>) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE.OPEN_PLATFORM_ROUTER,
            StorageKeyManager.genCorpUserKey(options)
        );
    }

    /** 管理后台左侧导航收起状态 */
    static getAdminNavCollapsedKey(options: User) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE.ADMIN_NAV_COLLAPSED,
            StorageKeyManager.genCorpUserKey(options)
        );
    }

    static getMemberTableSearchRangeKey(options: CorpUser) {
        return StorageKeyManager.genKey(
            LOCAL_STORAGE.MEMBER_TABLE_SEARCH_RANGE,
            StorageKeyManager.genCorpUserKey(options)
        );
    }
}
